import React, { createContext, useContext, useRef, useState } from "react";
import {
  CreditNote,
  CreditNoteItem,
  CREDIT_NOTE_REASON_RETURN_AND_PRICE_ADJUSTMENT,
  newCreditNote,
  newCreditNoteItem,
} from "../../../models/credit-note";
import Establishment from "../../../models/establishment";
import DispatchPoint from "../../../models/dispatch-point";
import Stamp from "../../../models/stamp";
import Client from "../../../models/client";
import { Invoice } from "../../../models/invoice";
import Errors from "../../../models/errors";
import { PYG } from "../../../currency/available-currencies";
import moment from "moment";
import { changeTZToUtc } from "../../../utils/dates";
import {
  ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE,
  ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE,
} from "../../../models/associated-document";

interface CreditNoteFormContextValue {
  // Refs and State
  creditNoteRef: React.MutableRefObject<CreditNote | null>;
  creditNoteDate: Date | undefined;
  currency: string;
  reasonCode: number;
  pygExchangeRate: number | undefined;
  totalAmount: number | undefined;
  amount10Percent: number | undefined;
  amount5Percent: number | undefined;
  amountExempt: number | undefined;
  iva10Percent: number | undefined;
  iva5Percent: number | undefined;
  establishment: Establishment | null;
  dispatchPoint: DispatchPoint | null;
  stamp: Stamp | null;
  client: Client | null;
  associatedDocumentType: string | undefined;
  electronicInvoice: Invoice | null;
  items: CreditNoteItem[];
  errors: Errors;
  readonly?: boolean;

  // Setters
  setCreditNoteDate: (date: Date | undefined) => void;
  setCurrency: (currency: string) => void;
  setReasonCode: (reasonCode: number) => void;
  setPygExchangeRate: (rate: number | undefined) => void;
  setEstablishment: (est: Establishment | null) => void;
  setDispatchPoint: (dp: DispatchPoint | null) => void;
  setStamp: (stamp: Stamp | null) => void;
  setClient: (client: Client | null) => void;
  setAssociatedDocumentType: (type: string | undefined) => void;
  setElectronicInvoice: (invoice: Invoice | null) => void;
  setItems: (items: CreditNoteItem[]) => void;
  setErrors: (errors: Errors) => void;

  // Handlers
  onEstablishmentChange: (newEstablishment: Establishment | null) => void;
  onDispatchPointChange: (newDispatchPoint: DispatchPoint | null) => void;
  onStampChange: (newStamp: Stamp | null) => void;
  onClientChange: (newClient: Client | null) => void;
  onCreditNoteDateChange: (value: string | moment.Moment) => void;
  onCurrencyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onReasonChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onAssociatedDocumentTypeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onElectronicInvoiceChange: (invoice: Invoice | null) => void;
  onAssociatedDocumentStampIdentifierChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentEstablishmentCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentDispatchPointCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentDateChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onPygExchangeRateChange: (newExchangeRate?: number) => void;
  onObsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemChange: (index: number, item: CreditNoteItem) => void;
  onItemDelete: (index: number) => void;
  onItemsChange: (newItems: CreditNoteItem[]) => void;
  onAddItemClick: () => void;
}

const CreditNoteFormContext = createContext<
  CreditNoteFormContextValue | undefined
>(undefined);

export const useCreditNoteFormContext = () => {
  const context = useContext(CreditNoteFormContext);
  if (!context) {
    throw new Error(
      "useCreditNoteFormContext must be used within CreditNoteFormProvider"
    );
  }
  return context;
};

interface CreditNoteFormProviderProps {
  children: React.ReactNode;
  taxPayerId?: number;
  readonly?: boolean;
}

export const CreditNoteFormProvider: React.FC<CreditNoteFormProviderProps> = ({
  children,
  taxPayerId,
  readonly,
}) => {
  const creditNoteRef = useRef<CreditNote | null>(newCreditNote(taxPayerId));
  const [creditNoteDate, setCreditNoteDate] = useState<Date | undefined>(
    undefined
  );
  const [currency, setCurrency] = useState(PYG);
  const [reasonCode, setReasonCode] = useState(
    CREDIT_NOTE_REASON_RETURN_AND_PRICE_ADJUSTMENT
  );
  const [pygExchangeRate, setPygExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [amount10Percent, setAmount10Percent] = useState<number | undefined>(
    undefined
  );
  const [amount5Percent, setAmount5Percent] = useState<number | undefined>(
    undefined
  );
  const [amountExempt, setAmountExempt] = useState<number | undefined>(
    undefined
  );
  const [iva10Percent, setIva10Percent] = useState<number | undefined>(
    undefined
  );
  const [iva5Percent, setIva5Percent] = useState<number | undefined>(undefined);
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null
  );
  const [dispatchPoint, setDispatchPoint] = useState<DispatchPoint | null>(
    null
  );
  const [stamp, setStamp] = useState<Stamp | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [associatedDocumentType, setAssociatedDocumentType] = useState<
    string | undefined
  >(undefined);
  const [electronicInvoice, setElectronicInvoice] = useState<Invoice | null>(
    null
  );
  const [items, setItems] = useState<CreditNoteItem[]>([]);
  const [errors, setErrors] = useState<Errors>({});

  const onEstablishmentChange = (newEstablishment: Establishment | null) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.establishmentId = newEstablishment?.id;
    creditNoteRef.current.establishmentCode = newEstablishment?.code;
    setEstablishment(newEstablishment);
  };

  const onDispatchPointChange = (newDispatchPoint: DispatchPoint | null) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.dispatchPointId = newDispatchPoint?.id;
    creditNoteRef.current.dispatchPointIdentifier =
      newDispatchPoint?.identifier;
    setDispatchPoint(newDispatchPoint);
  };

  const onStampChange = (newStamp: Stamp | null) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.stampId = newStamp?.id;
    creditNoteRef.current.stampIdentifier = newStamp?.identifier;
    creditNoteRef.current.stampBeginDate = newStamp?.beginDate;
    setStamp(newStamp);
  };

  const onClientChange = (newClient: Client | null) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.clientId = newClient?.id;
    creditNoteRef.current.clientCode = newClient?.code;
    creditNoteRef.current.clientIsTaxPayer = newClient?.isTaxPayer;
    creditNoteRef.current.clientRuc = newClient?.ruc;
    creditNoteRef.current.clientSocialName = newClient?.socialName;
    creditNoteRef.current.clientFantasyName = newClient?.fantasyName;
    creditNoteRef.current.clientType = newClient?.type;
    creditNoteRef.current.clientCountryCode = newClient?.countryCode;
    creditNoteRef.current.clientHasAddress = newClient?.hasAddress;
    creditNoteRef.current.clientAddress = newClient?.address;
    creditNoteRef.current.clientHouseNumber = newClient?.houseNumber;
    creditNoteRef.current.clientDepartment = newClient?.department;
    creditNoteRef.current.clientDepartmentDescription =
      newClient?.departmentDescription;
    creditNoteRef.current.clientDistrict = newClient?.district;
    creditNoteRef.current.clientDistrictDescription =
      newClient?.districtDescription;
    creditNoteRef.current.clientCity = newClient?.city;
    creditNoteRef.current.clientCityDescription = newClient?.cityDescription;
    creditNoteRef.current.clientPhoneNumber = newClient?.phoneNumber;
    creditNoteRef.current.clientEmail = newClient?.email;
    setClient(newClient);
  };

  const onCreditNoteDateChange = (value: string | moment.Moment) => {
    if (creditNoteRef.current === null) return;
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const creditNoteDate = new Date((value as moment.Moment).toISOString());
    setCreditNoteDate(creditNoteDate);
    creditNoteRef.current.creditNoteDate =
      changeTZToUtc(creditNoteDate).toISOString();
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.currency = e.target.value;
    setCurrency(e.target.value);
  };

  const onReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (creditNoteRef.current === null) return;
    const newReasonCode = Number(e.target.value);
    creditNoteRef.current.reasonCode = newReasonCode;
    setReasonCode(newReasonCode);
  };

  const onAssociatedDocumentTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (creditNoteRef.current === null) return;
    const newType = e.target.value;
    creditNoteRef.current.associatedDocumentType = newType;

    if (newType === ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE) {
      // Electronic invoice
      creditNoteRef.current.associatedDocumentPrintedType = undefined;
      creditNoteRef.current.associatedDocumentStampIdentifier = undefined;
      creditNoteRef.current.associatedDocumentEstablishmentCode = undefined;
      creditNoteRef.current.associatedDocumentDispatchPointCode = undefined;
      creditNoteRef.current.associatedDocumentNumber = undefined;
      creditNoteRef.current.associatedDocumentDate = undefined;
    } else if (newType === ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE) {
      // Printed invoice
      creditNoteRef.current.associatedDocumentCdc = undefined;
      creditNoteRef.current.invoiceId = undefined;
      creditNoteRef.current.associatedDocumentPrintedType =
        ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE;
      setElectronicInvoice(null);
    }

    setAssociatedDocumentType(newType);
  };

  const onElectronicInvoiceChange = (invoice: Invoice | null) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.invoiceId = invoice?.id;
    creditNoteRef.current.associatedDocumentCdc = invoice?.cdc;
    setElectronicInvoice(invoice);
  };

  const onAssociatedDocumentStampIdentifierChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.associatedDocumentStampIdentifier = e.target.value;
  };

  const onAssociatedDocumentEstablishmentCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.associatedDocumentEstablishmentCode = e.target.value;
  };

  const onAssociatedDocumentDispatchPointCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.associatedDocumentDispatchPointCode = e.target.value;
  };

  const onAssociatedDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.associatedDocumentNumber = e.target.value;
  };

  const onAssociatedDocumentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.associatedDocumentDate = e.target.value;
  };

  const onPygExchangeRateChange = (newExchangeRate?: number) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.pygExchangeRate = newExchangeRate;
    setPygExchangeRate(newExchangeRate);
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (creditNoteRef.current === null) return;
    creditNoteRef.current.obs = e.target.value;
  };

  const onItemChange = (index: number, item: CreditNoteItem) => {
    if (creditNoteRef.current === null) return;
    const newItems = [...creditNoteRef.current.items];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemDelete = (index: number) => {
    if (creditNoteRef.current === null) return;
    const newItems = [...creditNoteRef.current.items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  const onItemsChange = (newItems: CreditNoteItem[]) => {
    if (creditNoteRef.current === null) return;

    let newTotalAmount: number | undefined = undefined;
    let newAmount10Percent: number | undefined = undefined;
    let newAmount5Percent: number | undefined = undefined;
    let newAmountExempt: number | undefined = undefined;
    let newTaxed10Percent: number | undefined = undefined;
    let newTaxed5Percent: number | undefined = undefined;
    let newIva10Percent: number | undefined = undefined;
    let newIva5Percent: number | undefined = undefined;

    for (let item of newItems) {
      if (item.unitAmount !== undefined && item.quantity !== undefined) {
        newTotalAmount = newTotalAmount
          ? newTotalAmount + item.unitAmount * item.quantity
          : item.unitAmount * item.quantity;
      }

      if (item.amount10Percent !== undefined) {
        newAmount10Percent = newAmount10Percent
          ? newAmount10Percent + item.amount10Percent
          : item.amount10Percent;
      }

      if (item.amount5Percent !== undefined) {
        newAmount5Percent = newAmount5Percent
          ? newAmount5Percent + item.amount5Percent
          : item.amount5Percent;
      }

      if (item.amountExempt !== undefined) {
        newAmountExempt = newAmountExempt
          ? newAmountExempt + item.amountExempt
          : item.amountExempt;
      }

      if (item.taxed10Percent !== undefined) {
        newTaxed10Percent = newTaxed10Percent
          ? newTaxed10Percent + item.taxed10Percent
          : item.taxed10Percent;
      }

      if (item.taxed5Percent !== undefined) {
        newTaxed5Percent = newTaxed5Percent
          ? newTaxed5Percent + item.taxed5Percent
          : item.taxed5Percent;
      }

      if (item.iva10Percent !== undefined) {
        newIva10Percent = newIva10Percent
          ? newIva10Percent + item.iva10Percent
          : item.iva10Percent;
      }

      if (item.iva5Percent !== undefined) {
        newIva5Percent = newIva5Percent
          ? newIva5Percent + item.iva5Percent
          : item.iva5Percent;
      }
    }

    setTotalAmount(newTotalAmount);
    setAmount10Percent(newAmount10Percent);
    setAmount5Percent(newAmount5Percent);
    setAmountExempt(newAmountExempt);
    setIva10Percent(newIva10Percent);
    setIva5Percent(newIva5Percent);

    creditNoteRef.current = {
      ...creditNoteRef.current,
      items: newItems,
      amount: newTotalAmount,
      amount10Percent: newAmount10Percent,
      amount5Percent: newAmount5Percent,
      amountExempt: newAmountExempt,
      taxed10Percent: newTaxed10Percent,
      taxed5Percent: newTaxed5Percent,
      iva10Percent: newIva10Percent,
      iva5Percent: newIva5Percent,
    };

    setItems(newItems);
  };

  const onAddItemClick = () => {
    if (creditNoteRef.current === null) return;
    const newItems = [...creditNoteRef.current.items, newCreditNoteItem()];
    onItemsChange(newItems);
  };

  const value: CreditNoteFormContextValue = {
    creditNoteRef,
    creditNoteDate,
    currency,
    reasonCode,
    pygExchangeRate,
    totalAmount,
    amount10Percent,
    amount5Percent,
    amountExempt,
    iva10Percent,
    iva5Percent,
    establishment,
    dispatchPoint,
    stamp,
    client,
    associatedDocumentType,
    electronicInvoice,
    items,
    errors,
    readonly,
    setCreditNoteDate,
    setCurrency,
    setReasonCode,
    setPygExchangeRate,
    setEstablishment,
    setDispatchPoint,
    setStamp,
    setClient,
    setAssociatedDocumentType,
    setElectronicInvoice,
    setItems,
    setErrors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onClientChange,
    onCreditNoteDateChange,
    onCurrencyChange,
    onReasonChange,
    onAssociatedDocumentTypeChange,
    onElectronicInvoiceChange,
    onAssociatedDocumentStampIdentifierChange,
    onAssociatedDocumentEstablishmentCodeChange,
    onAssociatedDocumentDispatchPointCodeChange,
    onAssociatedDocumentNumberChange,
    onAssociatedDocumentDateChange,
    onPygExchangeRateChange,
    onObsChange,
    onItemChange,
    onItemDelete,
    onItemsChange,
    onAddItemClick,
  };

  return (
    <CreditNoteFormContext.Provider value={value}>
      {children}
    </CreditNoteFormContext.Provider>
  );
};
